<template>
	<div
		class="event-box"
		v-if="isEventActive"
		@click="openEventPopup"
		:data-event-type="attributes.type"
	>
		<div class="event-box__item">
			<AppNotification
				v-if="claimableMilestonesCount || marketHasNotification"
				class="event-box__notification"
				:type="StatusTypes.WARNING"
				:value="`${isMarketEvent ? '!' : claimableMilestonesCount}`"
			/>
			<Timer
				class="event-box__timer"
				:time="remainingSeconds"
				:has-icon="false"
				@timerFinished="handleRightboxEnd"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppNotification from "@/components/AppNotification.vue";
import Timer from "@/components/Timer.vue";
import {
	EVENTS_WITH_MILESTONES,
	LAST_SEEN_EVENT_ID_KEY,
	LAST_SEEN_EVENT_INTRO_ID_KEY,
	LAST_SEEN_EVENT_SHOP_ID_KEY,
	LAST_SEEN_EVENT_UPGRADES_ID_KEY,
} from "@/config/constants";
import { catchUpEventEnd, useEventStore } from "@/event";
import { isPastEndDate } from "@/event/session/eventEndDate";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSocketService from "@/hooks/useSocketService";
import useUserStore from "@/store/useUserStore";
import type { MilestoneItem } from "@/types/event";
import { MilestoneStatus } from "@/types/event";
import { EventCategory, StatusTypes } from "@/types/ui";
import { convertUnixTimeToRemainingSeconds } from "@/utils/format";
import { floorNumber } from "@/utils/number";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted, onUnmounted, ref, watch } from "vue";

const eventStore = useEventStore();
const {
	isActive: isEventActive,
	endDate,
	id,
	attributes,
	milestones,
	grandPrizeState,
} = storeToRefs(eventStore);

const userStore = useUserStore();
const { daysAfterRegistration } = storeToRefs(userStore);

const emitter = markRaw(useEmitter());
const socketService = markRaw(useSocketService());

const lastSeenEventIntroId = ref<string | null>(
	localStorage.getItem(LAST_SEEN_EVENT_INTRO_ID_KEY),
);

const lastSeenEventUpgradesId = ref(
	localStorage.getItem(LAST_SEEN_EVENT_UPGRADES_ID_KEY),
);

const lastSeenEventShopId = ref(
	localStorage.getItem(LAST_SEEN_EVENT_SHOP_ID_KEY),
);

const getStoredEventData = (): { id: string; timestamp: number } | null => {
	const stored = localStorage.getItem(LAST_SEEN_EVENT_ID_KEY);
	if (!stored) return null;
	try {
		return JSON.parse(stored);
	} catch {
		return null;
	}
};

const storedEventData = getStoredEventData();
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

if (storedEventData && Date.now() - storedEventData.timestamp > ONE_WEEK_MS) {
	localStorage.removeItem(LAST_SEEN_EVENT_ID_KEY);
}

const lastSeenEventId = ref<string | null>(getStoredEventData()?.id ?? null);

const hasMilestones = computed((): boolean => {
	if (!attributes.value.type) return false;

	return EVENTS_WITH_MILESTONES.includes(attributes.value.type);
});

const claimableMilestonesCount = computed((): number => {
	if (!hasMilestones.value || !milestones.value) return 0;
	const claimableCount = Array.from(milestones.value.values()).filter(
		(item: MilestoneItem): boolean =>
			item.status === MilestoneStatus.CLAIMABLE,
	).length;

	const claimablePremium = Array.from(milestones.value.values()).filter(
		(item: MilestoneItem): boolean =>
			item.premium?.status === MilestoneStatus.CLAIMABLE,
	).length;

	return claimableCount + claimablePremium;
});

const marketHasNotification = computed((): boolean => {
	if (
		grandPrizeState.value?.lastDailyFreePickedDaysAfterReg === undefined ||
		!isMarketEvent.value
	) {
		return false;
	}
	return (
		floorNumber(daysAfterRegistration.value) >
		grandPrizeState.value?.lastDailyFreePickedDaysAfterReg
	);
});

const isMarketEvent = computed((): boolean => {
	if (!attributes.value.type) return false;
	return [EventCategory.SOLAR, EventCategory.PIRATES].includes(
		attributes.value.type,
	);
});

const openEventPopup = (): void => {
	if (isPastEndDate(endDate.value)) return;

	if (id.value) {
		const data = { id: id.value, timestamp: Date.now() };
		localStorage.setItem(LAST_SEEN_EVENT_ID_KEY, JSON.stringify(data));
		lastSeenEventId.value = id.value;
		emitter.emit(EmitterEvents.EventLocalStorageUpdated);
	}
	eventStore.setEventPopupActive(true);
};

const remainingSeconds = ref<number>(0);

const calcRemainingSeconds = (): number => {
	if (!endDate.value) return 0;

	return convertUnixTimeToRemainingSeconds(
		(endDate.value.getTime() ?? 0) / 1_000,
	);
};

watch(
	(): Date | null => endDate.value,
	(): void => {
		remainingSeconds.value = calcRemainingSeconds();
	},
	{ immediate: true },
);

const handleRightboxEnd = (): void => {
	void socketService.getEventOverview().catch((): void => {});
};

const recalculateEvent = (): void => {
	remainingSeconds.value = calcRemainingSeconds();
	catchUpEventEnd();
};

const updateLocalStorageData = (): void => {
	lastSeenEventIntroId.value = localStorage.getItem(
		LAST_SEEN_EVENT_INTRO_ID_KEY,
	);
	lastSeenEventUpgradesId.value = localStorage.getItem(
		LAST_SEEN_EVENT_UPGRADES_ID_KEY,
	);
	lastSeenEventShopId.value = localStorage.getItem(
		LAST_SEEN_EVENT_SHOP_ID_KEY,
	);
	lastSeenEventId.value = getStoredEventData()?.id ?? null;
};

onMounted((): void => {
	emitter.on(EmitterEvents.EventRecalc, recalculateEvent);
	emitter.on(EmitterEvents.EventLocalStorageUpdated, updateLocalStorageData);
});

onUnmounted((): void => {
	emitter.off(EmitterEvents.EventRecalc, recalculateEvent);
	emitter.off(EmitterEvents.EventLocalStorageUpdated, updateLocalStorageData);
});
</script>

<style lang="scss" scoped>
.event-box {
	position: relative;
	margin-right: 0.5rem;
	width: 4.375rem;
	height: 5.625rem;
	@include clickable-element;
	@include background(
		url($path-events + "christmas/right-boxes/rightbox-christmas.avif"),
		contain,
		center,
		no-repeat
	);
	z-index: 0; // IMPORTANT: to prevent notifications from being displayed over modal windows

	&__notification {
		position: absolute;
		top: -0.2rem;
		right: -0.2rem;
		z-index: 1;
	}

	&__timer {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 1.313rem;
		height: 1.5rem;
		color: #f2dcb5;
		font-size: 0.75rem;
		text-align: center;
	}
	&[data-event-type="christmas"] {
		@include background(
			url($path-events + "christmas/right-boxes/rightbox-christmas.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="easter"] {
		.event-box__notification {
			top: 1.5rem;
			right: 0.3rem;
		}
		@include background(
			url($path-events + "easter/right-boxes/rightbox-easter.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="fishing"] {
		.event-box__notification {
			top: 1.5rem;
			right: 0.3rem;
		}
		@include background(
			url($path-events + "fishing/right-boxes/rightbox-fishing.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="firefest"] {
		.event-box__notification {
			top: 1.5rem;
			right: 0.3rem;
		}
		@include background(
			url($path-events + "firefest/right-boxes/rightbox-firefest.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="solarfair"] {
		.event-box__notification {
			top: 1.5rem;
			right: 0.3rem;
		}
		@include background(
			url($path-events + "solarfair/right-boxes/rightbox-solarfair.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="hunting"] {
		.event-box__notification {
			top: 1.5rem;
			right: 0.3rem;
		}
		@include background(
			url($path-events + "hunting/right-boxes/rightbox-hunting.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="halloween"] {
		.event-box__notification {
			top: 1.5rem;
			right: 0.3rem;
		}
		@include background(
			url($path-events + "halloween/right-boxes/rightbox-halloween.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="pirates"] {
		.event-box__notification {
			top: 1.5rem;
			right: 0.3rem;
		}
		@include background(
			url($path-events + "pirates/right-boxes/rightbox-pirates.avif"),
			contain,
			center,
			no-repeat
		);
	}
}
</style>
